import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Mail, Clock, Shield, ArrowRight, Home, MessageSquare, Sparkles, GraduationCap, Briefcase } from 'lucide-react';
import { useEffect } from 'react';

export const ThankYouPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('report_id');

  useEffect(() => {
    // Only redirect if not loading and user is null
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Show loading while auth is being checked
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
          <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isStudent = user?.user_type === 'student';

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="thank-you-page">
      {/* Background effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-[#CC5500]/10 rounded-full filter blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-[#C5A065]/10 rounded-full filter blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#CC5500]/20 to-[#C5A065]/20 border-2 border-[#CC5500]/30">
            <CheckCircle className="h-12 w-12 text-[#CC5500]" />
          </div>
        </div>

        {/* Main Message */}
        <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">
          Assessment Complete
        </h1>
        <p className="text-xl text-[#C5A065] mb-6">
          Thank you, {user?.full_name}!
        </p>

        {/* Description */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-8 text-left">
          <p className="text-lg text-gray-300 mb-6">
            Your {isStudent ? 'career discovery' : 'leadership intelligence'} profile has been generated and is now under review by our team at <strong className="text-white">LeadEconomics</strong>.
          </p>

          {/* Timeline */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#CC5500]/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-[#CC5500]" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Assessment Completed</h3>
                <p className="text-sm text-gray-400">Your responses have been securely recorded</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C5A065]/20 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-[#C5A065]" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Expert Review</h3>
                <p className="text-sm text-gray-400">Our consultants will review your profile</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <div>
                <h3 className="text-gray-400 font-semibold">Report Delivery</h3>
                <p className="text-sm text-gray-500">Your comprehensive report will be delivered via email</p>
              </div>
            </div>
          </div>
        </div>

        {/* Q&A Feature Highlight */}
        <div className="bg-gradient-to-r from-[#CC5500]/20 to-[#C5A065]/10 border border-[#CC5500]/30 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-3 justify-center">
            <Sparkles className="h-5 w-5 text-[#CC5500]" />
            <span className="text-[#CC5500] font-semibold">NEW: Ask the AI About Your Report</span>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            While you wait for your full report, chat with our AI to get instant insights about your assessment. 
            <strong className="text-white"> 2 questions FREE!</strong>
          </p>
          <Button
            onClick={() => navigate(`/report-qa?report_id=${reportId}`)}
            className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white"
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            Chat with Report AI
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="h-5 w-5 text-[#C5A065]" />
            <span className="text-[#C5A065] font-semibold">What's Next?</span>
          </div>
          <p className="text-sm text-gray-400 text-left">
            Your {isStudent ? 'career discovery' : 'leadership intelligence'} report will be reviewed and delivered to you within 24-48 hours via email.
          </p>
        </div>

        {/* Career Counselling / Mentoring Section - After Test Completion */}
        <div className="bg-gradient-to-r from-[#C5A065]/10 to-[#CC5500]/10 backdrop-blur-xl border border-[#C5A065]/30 rounded-2xl p-6 mb-8" data-testid="counselling-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#C5A065] to-[#CC5500] flex items-center justify-center">
              {isStudent ? (
                <GraduationCap className="h-5 w-5 text-white" />
              ) : (
                <Briefcase className="h-5 w-5 text-white" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-white">
              {isStudent ? 'Career Counselling Session' : 'Executive Mentoring'}
            </h3>
          </div>
          <p className="text-gray-400 text-sm mb-4 text-left">
            {isStudent 
              ? 'Get personalized 1-on-1 guidance from our expert career counsellors to unlock your full potential based on your assessment results.'
              : 'Elevate your leadership with personalized executive mentoring from our expert consultants. Get tailored strategies based on your leadership profile.'
            }
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-[#C5A065] font-semibold text-lg" data-testid="session-fees">
                <span className="text-gray-400 text-sm font-normal">Starting from </span>
                {isStudent ? '₹50,000' : '₹1,25,000'} <span className="text-gray-500 text-sm font-normal">or</span> {isStudent ? '$750' : '$1,500'}
              </p>
              <p className="text-gray-500 text-xs">{isStudent ? 'Book your career counselling session' : 'Transform your leadership journey'}</p>
            </div>
            <Button
              variant="outline"
              className="border-[#C5A065] text-[#C5A065] hover:bg-[#C5A065]/10"
              onClick={() => window.open(`mailto:admin@leadeconomics.in?subject=${isStudent ? 'Career%20Counselling' : 'Executive%20Mentoring'}%20Session%20Booking&body=Hi,%0A%0AI%20have%20completed%20my%20assessment%20(Report%20ID:%20${reportId?.slice(0, 8).toUpperCase() || 'N/A'})%20and%20would%20like%20to%20${isStudent ? 'book%20a%20career%20counselling' : 'connect%20with%20a%20mentor'}%20session.%0A%0AThank%20you.`, '_blank')}
              data-testid="book-session-btn"
            >
              {isStudent ? 'Book Now' : 'Connect me to a Mentor'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="lg"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Home className="mr-2 h-4 w-4" />
            Return to Home
          </Button>
          <Button
            onClick={handleLogout}
            size="lg"
            className="bg-white/10 border border-white/20 text-white hover:bg-white/20"
          >
            Sign Out
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-600 mt-8">
          Report ID: {reportId?.slice(0, 8).toUpperCase() || 'N/A'} • Confidential {isStudent ? 'Career' : 'Leadership'} Intelligence
        </p>
      </div>
    </div>
  );
};
